import Head from 'next/head';
import React, { useState, useEffect } from 'react';
import Image from 'next/image';
import IMAGES from '@/public/assets/img';
import { WEB_URL } from '@/config/endpoints';
import { useRouter } from 'next/router';
import useIsMobile from '@/shared/useIsMobile';
import credlixremit from '@/config/credlixremit_config';
import AccordianFaq from '@/component/shared/accordianfaq';
import { generateFaqSchema } from '@/component/shared/faqSchema';
import WhyExportersMove from '@/component/credlixremit/whyExportersMove';
import HowItWorks from '@/component/credlixremit/howItWorks';
import CompareOptions from '@/component/credlixremit/compareOptions';
import MarginLeakage from '@/component/credlixremit/marginLeakage';
import CredlixremitForm from '@/component/credlixremit/credlixremitForm';

const Credlixremit = () => {
  const router = useRouter();
  const isMobile = useIsMobile();
  const faqSchema = generateFaqSchema(credlixremit.Faq);
  const [hideBanner, setHideBanner] = useState(false);

  useEffect(() => {
    if (router.query?.source === 'inrInfo' || router.asPath?.includes('source=inrInfo')) {
      setHideBanner(true);
    }
  }, [router.query, router.asPath]);

  return (
    <main className="pt-[110px]">
      <Head>
        <title>{credlixremit.seoData.title}</title>
        <meta
          name="description"
          content={credlixremit.seoData.description}
        />
        <meta property="og:title" content={credlixremit.seoData.title} />
        <meta
          property="og:description"
          content={credlixremit.seoData.description}
        />
        <meta property="og:image" content={IMAGES.HOMEIMG.bigLogoNew.src} />
        <meta property="og:url" content={WEB_URL + router.asPath} />
        <link rel="canonical" href={WEB_URL + router.asPath} />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(faqSchema),
          }}
        />
      </Head>

      {!hideBanner && (
        <section className="relative bg-no-repeat sm:bg-center bg-cover bg-bottom pt-8 pb-12 min-h-[550px] sm:min-h-fit flex items-center">
          {!isMobile ? (
            <Image
              priority={true}
              src={IMAGES.inrxpress.inrxpressBgDp}
              width={1366}
              height={600}
              className="absolute inset-0 max-w-none w-full h-full object-cover"
              alt="Banner Desktop"
            />
          ) : (
            <Image
              priority={true}
              src={IMAGES.inrxpress.inrxpressBgMobile}
              width={767}
              height={500}
              className="absolute inset-0 max-w-none w-full h-full object-cover"
              alt="Banner Mobile"
            />
          )}
          <div className="container relative px-2 sm:px-3 flex justify-between items-center sm:flex-col sm:pt-1 gap-4">
            <div className="w-1/2 sm:w-full flex flex-col items-start z-10">
              <div className="flex items-center gap-2 sm:gap-1 mb-2 sm:mb-0">
                {IMAGES.inrxpress?.credlixremitLogo && (
                  <Image
                    src={IMAGES.inrxpress.credlixremitLogo}
                    alt="CredlixRemit Logo"
                    width={260}
                    height={70}
                    className="sm:h-11 sm:w-4/6 w-auto object-contain"
                    priority
                  />
                )}
                {credlixremit.logoTagline && (
                  <p
                    className="border-l border-bordercolor pl-2 sm:pl-1 text-[#022B5C] text-sm sm:text-[11px] font-medium"
                    dangerouslySetInnerHTML={{ __html: credlixremit.logoTagline }}
                  />
                )}
              </div>
              <h2 className="text-baseBlack text-xxl sm:text-lg font-medium mt-1">
                {credlixremit.bannerHeading}
              </h2>
              <h1 className="text-heading5 sm:text-xxl font-bold mb-5 sm:mb-1 bg-clip-text text-transparent bg-[linear-gradient(90deg,#022D60_0%,#0363C4_58%,#086AC9_100%)]">
                {credlixremit.bannerSubHeading}
              </h1>
                <div className="mt-2 w-full">
                  <Image
                    src={IMAGES.inrxpress.inrxpressCurrency}
                    alt="USD EUR GBP to INR Settlement Flow"
                    width={536}
                    height={252}
                    quality={100}
                    className="w-full h-auto object-cover"
                    priority
                  />
                </div>
            </div>
            <div className="w-[47%] sm:w-full">
              <CredlixremitForm data={credlixremit} />
            </div>
          </div>
        </section>
      )}

      <MarginLeakage data={credlixremit.marginLeakage} />
      <CompareOptions data={credlixremit.compareOptions} />
      <HowItWorks data={credlixremit.howItWorks} />
      <WhyExportersMove data={credlixremit.whyExportersMove} />

      <section className="py-sp-100 sm:py-sp-big bg-[#022B5C]">
        <div className="container px-3 lg:px-3 xm:px-2">
          <h2 className="font-medium text-center text-white text-xxxl sm:text-lg">{credlixremit.ConuterSec.heading}</h2>
          <div className="flex justify-between flex-wrap w-[80%] sm:w-full mx-auto">
            {credlixremit.ConuterSec.counterList.map((item, i) => (
              <div key={i} className="w-[22%] sm:w-full pt-4 px-3">
                <h3 className="text-heading4 text-white text-center mb-xsp flex items-center justify-center"> {item.heading} <strong className="text-textxl text-[#FECC2F] font-medium mt-1">{item.smalltext}</strong></h3>
                <p className="text-base font-medium text-center text-[#C6D3E4]">{item.paragraph}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-white py-sp-100 sm:py-sp-big flex flex-col items-center">
        <div className="container px-3 xm:px-2 ">
          <h3 className="mb-6 font-medium text-xxxl sm:text-lg text-center text-[#2C2C2C]">
            {credlixremit.FaqHead}
          </h3>
          <AccordianFaq faqData={credlixremit.Faq} />
        </div>
      </section>
    </main>
  );
};

export default Credlixremit;